import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Lock, Phone, Mail, MessageSquare } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  UniversalEmptyState,
  Card,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  StatusBadge,
  type UniversalTableColumn,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  salesService,
  type CustomerStatusRow,
  type CustomerHealthStatus,
} from '@/domains/sales/services/salesService';
import { AddNoteForm } from '../components/AddNoteForm';

/**
 * SalesCustomersPage — converted customers list with health-status badges
 * + at-risk filter + click-through to a read-only customer card for win-back
 * outreach.
 *
 * SALES-WIN-1 (2026-06-05): replaces the previous text-only listing with the
 * v_sales_customer_status view so managers can spot churning customers and
 * call them back per LD-S12.
 *
 * LD-S11 envelope: page is fully read-only. No edit, no impersonation, no
 * ticket/cPanel/full-billing access. Outstanding balance + last-paid date are
 * exposed as minimum facts for win-back conversations only.
 */

const AT_RISK_STATUSES: ReadonlySet<CustomerHealthStatus> = new Set([
  'payment_pending',
  'overdue',
  'suspended',
  'terminated',
]);

function statusBadge(status: CustomerHealthStatus) {
  switch (status) {
    case 'active':
      return <StatusBadge tone="active">Active</StatusBadge>;
    case 'payment_pending':
      return <StatusBadge tone="pending">Payment pending</StatusBadge>;
    case 'overdue':
      return <StatusBadge tone="overdue">Overdue</StatusBadge>;
    case 'suspended':
      return <StatusBadge tone="overdue">Suspended</StatusBadge>;
    case 'terminated':
      return <StatusBadge tone="draft">Terminated</StatusBadge>;
    default:
      return <StatusBadge tone="info">{status}</StatusBadge>;
  }
}

function formatZAR(amount: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount ?? 0);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA');
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export default function SalesCustomersPage() {
  const [rows, setRows] = useState<CustomerStatusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [selected, setSelected] = useState<CustomerStatusRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    salesService
      .listCustomerStatus()
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * STOCK — these are current customers. The date is `assigned_at`, so the
   * control says `Won`: the day the lead became this agent's customer. Calling
   * it "Created" would name a different event (the profile row), and calling it
   * "Period" would quietly reframe "my customers" as "customers won in period".
   */
  const table = useTableFilters<CustomerStatusRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Won',
    dateOf: (r) => r.assigned_at,
    searchText: (r) => [r.full_name, r.email ?? '', r.phone ?? '', r.company ?? ''].join(' '),
    facets: useMemo(
      () => [(r: CustomerStatusRow) => !atRiskOnly || AT_RISK_STATUSES.has(r.health_status)],
      [atRiskOnly],
    ),
  });

  const filtered = table.rows;

  const atRiskCount = useMemo(
    () => rows.filter((r) => AT_RISK_STATUSES.has(r.health_status)).length,
    [rows]
  );

  const columns = useMemo<UniversalTableColumn<CustomerStatusRow>[]>(
    () => [
      {
        key: 'full_name',
        header: 'Customer',
        render: (r) => <span className="font-medium">{r.full_name}</span>,
      },
      { key: 'company', header: 'Company', render: (r) => r.company ?? '—' },
      { key: 'health_status', header: 'Status', render: (r) => statusBadge(r.health_status) },
      {
        key: 'outstanding_balance',
        header: 'Outstanding',
        align: 'right',
        render: (r) => (
          <span className={r.outstanding_balance > 0 ? 'font-medium text-danger' : 'text-muted-foreground'}>
            {formatZAR(r.outstanding_balance)}
          </span>
        ),
      },
      {
        key: 'last_paid_at',
        header: 'Last paid',
        render: (r) => formatDate(r.last_paid_at),
      },
      {
        key: 'assigned_at',
        header: 'Converted',
        render: (r) => formatDate(r.assigned_at),
      },
    ],
    []
  );

  return (
    <UniversalPage
      title="Customers"
      description="Customers you brought in — read-only contact details + health status for win-back outreach (LD-S11 / LD-S12)."
    >
      <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
        <Button
          size="sm"
          variant={atRiskOnly ? 'primary' : 'outline'}
          onClick={() => setAtRiskOnly((v) => !v)}
          aria-pressed={atRiskOnly}
        >
          <AlertTriangle className="h-4 w-4" />
          At risk ({atRiskCount})
        </Button>
        <span className="text-xs text-muted-foreground">
          {atRiskOnly
            ? `Showing ${filtered.length} at-risk customer${filtered.length === 1 ? '' : 's'} (overdue / suspended / terminated / payment pending).`
            : `Showing ${filtered.length} total. Click a row for contact details + win-back facts.`}
        </span>
      </Card>

      {error && <Card className="mb-4 border-danger/30 bg-danger/5 p-4 text-sm text-danger">{error}</Card>}

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : filtered.length === 0 ? (
        <UniversalEmptyState
          title={atRiskOnly ? 'No at-risk customers' : 'No converted customers yet'}
          description={
            atRiskOnly
              ? 'Nothing to win back right now — every converted customer is paying on time. Switch off the filter to see your full cohort.'
              : "When you convert a lead, they appear here once they've paid their first invoice."
          }
        />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <PeriodFilter
              value={table.filters.period}
              from={table.filters.from}
              to={table.filters.to}
              onChange={table.filters.setPeriod}
              onStep={table.stepPeriodBy}
              presets={TABLE_PRESETS}
              variant="menu"
              steppable
              label="Won"
            />
            <SearchField
              value={table.q}
              onValueChange={table.setQ}
              pending={table.isSearching}
              placeholder="Search name, email, phone, company"
              className="ml-auto w-full max-w-xs"
            />
          </div>
        <UniversalTable
          columns={columns}
          data={filtered}
          caption={table.caption}
          getRowKey={(r) => r.customer_id}
          onRowClick={(r) => setSelected(r)}
        />
        </>
      )}

      {selected && <CustomerCard customer={selected} onClose={() => setSelected(null)} />}
    </UniversalPage>
  );
}

function CustomerCard({ customer, onClose }: { customer: CustomerStatusRow; onClose: () => void }) {
  const days = daysSince(customer.last_paid_at);
  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={customer.full_name}
      description={customer.company ?? 'No company on record'}
      size="wide"
    >
      <ModalBody>
        <Card className="mb-4 border-warning/30 bg-warning/5 p-3">
          <div className="flex items-start gap-3">
            <Lock className="h-4 w-4 shrink-0 text-warning-dark mt-0.5" aria-hidden />
            <p className="text-xs text-muted-foreground">
              SightHost-managed customer — read-only view for win-back outreach. You can call, email, and log
              notes for your own follow-up; you cannot mutate the customer's account. Operational data
              (tickets, billing detail, cPanel) belongs to SightHost.
            </p>
          </div>
        </Card>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {statusBadge(customer.health_status)}
          <span className="text-xs text-muted-foreground">
            Lifecycle stage: <code className="text-xs">{customer.lifecycle_stage}</code>
          </span>
        </div>

        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Email" value={customer.email}>
            {customer.email && (
              <a className="text-xs text-primary-ink hover:underline" href={`mailto:${customer.email}`}>
                <Mail className="inline h-3 w-3" /> Send email
              </a>
            )}
          </Field>
          <Field label="Phone" value={customer.phone}>
            {customer.phone && (
              <a className="text-xs text-primary-ink hover:underline" href={`tel:${customer.phone}`}>
                <Phone className="inline h-3 w-3" /> Call
              </a>
            )}
          </Field>
          <Field label="Outstanding balance" value={formatZAR(customer.outstanding_balance)} valueClassName={customer.outstanding_balance > 0 ? 'font-medium text-danger' : undefined} />
          <Field label="Last paid" value={formatDate(customer.last_paid_at)}>
            {days !== null && (
              <span className="text-xs text-muted-foreground">{days} day{days === 1 ? '' : 's'} ago</span>
            )}
          </Field>
          <Field label="Converted" value={formatDate(customer.assigned_at)} />
        </dl>

        {AT_RISK_STATUSES.has(customer.health_status) && (
          <Card className="mt-4 border-info/30 bg-info/5 p-3 text-xs text-foreground">
            <strong>Win-back tip:</strong> open the call with empathy — money trouble is the #1 cause of churn. Guide them to{' '}
            <code>portal → Pay overdue invoice</code> on the phone. You cannot pay on their behalf. If they need a billing
            concession, ticket the <code>Sales</code> department for SightHost to handle.
          </Card>
        )}

        <section className="mt-6">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
            <MessageSquare className="h-4 w-4" /> Add a note
          </h3>
          <p className="mb-2 text-xs text-muted-foreground">
            Log call outcomes or win-back context. Notes are sales-side only and append-only.
          </p>
          <AddNoteForm customerProfileId={customer.customer_id} placeholder="Called about renewal — will pay Friday…" />
        </section>
      </ModalBody>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function Field({
  label,
  value,
  children,
  valueClassName,
}: {
  label: string;
  value: string | null;
  children?: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 text-sm text-ink ${valueClassName ?? ''}`}>{value ?? '—'}</dd>
      {children && <div className="mt-1">{children}</div>}
    </div>
  );
}
